/** Shared entity shapes. These are the wire format between API, sync feed and client store. */

export type ViewStyle = "list" | "board" | "calendar";
/** 4 = P1 (urgent) down to 1 = P4 (none), matching Todoist's wire format. */
export type Priority = 1 | 2 | 3 | 4;
export type Theme = "system" | "light" | "dark";

export interface Due {
  /** ISO date, YYYY-MM-DD */
  date: string;
  /** ISO datetime (UTC) when a time was given, otherwise null */
  datetime: string | null;
  /** IANA timezone the datetime was set in */
  timezone: string | null;
  /** Original human text, e.g. "every monday at 9am" */
  string: string;
  /** True when `string` describes a recurrence */
  isRecurring: boolean;
}

interface Base {
  id: string;
  version: number;
  updatedAt: string;
  deletedAt: string | null;
}

export interface Project extends Base {
  name: string;
  color: string;
  parentId: string | null;
  childOrder: number;
  isFavorite: boolean;
  isArchived: boolean;
  isInbox: boolean;
  viewStyle: ViewStyle;
  createdBy: string;
}

export interface Section extends Base {
  projectId: string;
  name: string;
  sectionOrder: number;
  isArchived: boolean;
}

export interface Task extends Base {
  projectId: string;
  sectionId: string | null;
  parentId: string | null;
  content: string;
  description: string;
  priority: Priority;
  due: Due | null;
  deadline: string | null;
  durationMinutes: number | null;
  childOrder: number;
  dayOrder: number;
  labelIds: string[];
  assigneeId: string | null;
  createdBy: string;
  completedAt: string | null;
  isCollapsed: boolean;
}

export interface Label extends Base {
  name: string;
  color: string;
  itemOrder: number;
  isFavorite: boolean;
}

export interface Attachment {
  fileId: string;
  name: string;
  size: number;
  mime: string;
}

/** A comment on a task (Todoist calls these notes). */
export interface Comment extends Base {
  taskId: string;
  projectId: string;
  userId: string;
  content: string;
  attachment: Attachment | null;
  createdAt: string;
}

/** A saved filter (smart list) using the Todoist-style query language. */
export interface Filter extends Base {
  name: string;
  query: string;
  color: string;
  itemOrder: number;
  isFavorite: boolean;
}

export interface Reminder extends Base {
  taskId: string;
  userId: string;
  /** absolute: fires at `at`; relative: fires `minuteOffset` minutes before the task's due time */
  type: "absolute" | "relative";
  at: string | null;
  minuteOffset: number | null;
}

export interface Habit extends Base {
  name: string;
  color: string;
  icon: string;
  /** Target check-ins per week (7 = daily) */
  targetPerWeek: number;
  itemOrder: number;
  isArchived: boolean;
  /** Local time "HH:MM" for a daily push reminder, or null */
  reminderTime: string | null;
}

export interface HabitCheckin extends Base {
  habitId: string;
  /** ISO date, YYYY-MM-DD */
  date: string;
}

export interface FocusSession extends Base {
  taskId: string | null;
  kind: "pomodoro" | "stopwatch";
  startedAt: string;
  endedAt: string;
  durationSec: number;
}

/** Another member of the workspace. */
export interface Collaborator {
  id: string;
  name: string;
  email: string;
  image: string | null;
  isAdmin: boolean;
}

export interface Membership {
  projectId: string;
  userId: string;
  role: "owner" | "member";
}

export interface User {
  id: string;
  name: string;
  email: string;
  image: string | null;
  inboxProjectId: string | null;
  isAdmin: boolean;
  timezone: string;
  dailyGoal: number;
  weeklyGoal: number;
  vacationMode: boolean;
  /** 0 = Sunday, 1 = Monday */
  startOfWeek: number;
  theme: Theme;
  /** Minutes before a timed task to send an automatic push reminder; null = off */
  autoReminderMinutes: number | null;
  /** Local time "HH:MM" for the daily summary push, or null */
  dailyDigestTime: string | null;
}

export const PROJECT_COLORS = [
  "berry_red", "red", "orange", "yellow", "olive_green", "lime_green", "green", "mint_green",
  "teal", "sky_blue", "light_blue", "blue", "grape", "violet", "lavender", "magenta",
  "salmon", "charcoal", "grey", "taupe",
] as const;
export type ProjectColor = (typeof PROJECT_COLORS)[number];

export const COLOR_HEX: Record<ProjectColor, string> = {
  berry_red: "#b8255f", red: "#db4035", orange: "#ff9933", yellow: "#fad000", olive_green: "#afb83b",
  lime_green: "#7ecc49", green: "#299438", mint_green: "#6accbc", teal: "#158fad", sky_blue: "#14aaf5",
  light_blue: "#96c3eb", blue: "#4073ff", grape: "#884dff", violet: "#af38eb", lavender: "#eb96eb",
  magenta: "#e05194", salmon: "#ff8d85", charcoal: "#808080", grey: "#b8b8b8", taupe: "#ccac93",
};

export const colorHex = (c: string): string => COLOR_HEX[c as ProjectColor] ?? "#808080";
